import { useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
} from 'reactstrap';
import { Btn } from '../../../AbstractElements';
import { toast } from 'react-toastify';
import CaseFollowUpService from '../../../Api/services/case-follow-up.service';
import { useTranslation } from 'react-i18next';
import type { components } from '../../../Api/types/openapi';

type CreateCaseFollowUp = components['schemas']['CreateCaseFollowUp'];
type CaseTypeEnum = components['schemas']['CaseTypeEnum'];

interface CaseFollowUpFormProps {
  isOpen: boolean;
  toggle: () => void;
  onSuccess: () => void;
}

const CaseFollowUpForm: React.FC<CaseFollowUpFormProps> = ({
  isOpen,
  toggle,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<CreateCaseFollowUp>({
    name: '',
    case_type: 'USCIS',
    case_number_uscis: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validaciones
    if (!formData.name || formData.name.trim().length < 3) {
      toast.error(t('El nombre es requerido'));
      return;
    }
    if (!formData.case_number_uscis || formData.case_number_uscis.trim().length === 0) {
      toast.error(t('El número de caso USCIS es requerido'));
      return;
    }
    if (!formData.case_type) {
      toast.error(t('El tipo de caso es requerido'));
      return;
    }

    try {
      setLoading(true);
      await CaseFollowUpService.createLawyerAlias(formData);
      toast.success(t('Alias creado exitosamente'));
      setFormData({
        name: '',
        case_type: 'USCIS',
        case_number_uscis: '',
      });
      toggle();
      onSuccess();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || t('Error al crear alias');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: '',
        case_type: 'USCIS',
        case_number_uscis: '',
      });
      toggle();
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <ModalHeader toggle={handleClose}>{t('Crear Alias')}</ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <FormGroup>
            <Label>{t('Alias/Nombre')} *</Label>
            <Input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={t('Ingrese un nombre o alias para el caso')}
              required
              disabled={loading}
            />
          </FormGroup>

          <FormGroup>
            <Label>{t('Tipo de Caso')} *</Label>
            <Input
              type="select"
              name="case_type"
              value={formData.case_type}
              onChange={handleChange}
              required
              disabled={loading}
            >
              <option value="USCIS">{t('USCIS')}</option>
              <option value="EOIR">{t('EOIR')}</option>
              <option value="CEAC">{t('CEAC')}</option>
            </Input>
          </FormGroup>

          <FormGroup>
            <Label>{t('Número de Caso USCIS')} *</Label>
            <Input
              type="text"
              name="case_number_uscis"
              value={formData.case_number_uscis}
              onChange={handleChange}
              placeholder="Ej: MSC1234567890"
              required
              disabled={loading}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Btn color="secondary" onClick={handleClose} disabled={loading}>
            {t('Cancelar')}
          </Btn>
          <Btn color="primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <Spinner size="sm" className="me-1" />
                {t('Creando...')}
              </>
            ) : (
              t('Crear')
            )}
          </Btn>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default CaseFollowUpForm;

